import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  UserCog,
  Key,
  Shield,
  ShieldAlert,
  ShieldCheck,
  UserX,
  UserCheck,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleOptions = [
  { value: "admin", label: "Admin", icon: ShieldAlert },
  { value: "manager", label: "Manager", icon: ShieldCheck },
  { value: "worker", label: "Technician", icon: Shield },
  { value: "customer", label: "Customer", icon: Users },
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "worker",
    customer_id: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are module-level constants
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true,
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are module-level constants
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setCurrentUser(response.data);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- API and axios are module-level constants
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        withCredentials: true,
      });
      // Filter to only show customers without a user account
      setCustomers(response.data.filter(c => !c.user_id));
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
    fetchCustomers();
  }, [fetchUsers, fetchCurrentUser, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
        };
        // Include customer_id if switching to customer role
        if (formData.role === "customer" && formData.customer_id) {
          updateData.customer_id = formData.customer_id;
        }
        await axios.put(`${API}/users/${editingUser.user_id}`, updateData, {
          withCredentials: true,
        });
        toast.success("User updated successfully");
      } else {
        if (formData.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          return;
        }
        // Validate customer selection for customer role
        if (formData.role === "customer" && !formData.customer_id) {
          toast.error("Please select a customer profile to link");
          return;
        }
        const createData = { ...formData };
        if (formData.role !== "customer") {
          delete createData.customer_id;
        }
        await axios.post(`${API}/users`, createData, {
          withCredentials: true,
        });
        toast.success("User created successfully");
      }
      setSheetOpen(false);
      resetForm();
      fetchUsers();
      fetchCustomers(); // Refresh customers list
    } catch (error) {
      console.error("Error saving user:", error);
      const message = error.response?.data?.detail || "Failed to save user";
      toast.error(message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      customer_id: user.customer_id || "",
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/users/${userToDelete.user_id}`, {
        withCredentials: true,
      });
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      const message = error.response?.data?.detail || "Failed to delete user";
      toast.error(message);
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await axios.post(
        `${API}/users/${userToReset.user_id}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success("Password reset successfully");
      setResetPasswordDialogOpen(false);
      setUserToReset(null);
      setNewPassword("");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Failed to reset password");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const response = await axios.post(
        `${API}/users/${user.user_id}/toggle-active`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      console.error("Error toggling user status:", error);
      const message = error.response?.data?.detail || "Failed to update user status";
      toast.error(message);
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "worker",
      customer_id: "",
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-50 text-red-700 ring-red-600/20";
      case "manager":
        return "bg-blue-50 text-blue-700 ring-blue-600/20";
      case "customer":
        return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
      default:
        return "bg-slate-50 text-slate-700 ring-slate-600/20";
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const adminCount = users.filter((u) => u.role === "admin").length;
  const managerCount = users.filter((u) => u.role === "manager").length;
  const workerCount = users.filter((u) => u.role === "worker").length;
  const customerCount = users.filter((u) => u.role === "customer").length;

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage users, roles, and permissions</p>
        </div>
        <Button
          className="rounded-sm bg-slate-900 hover:bg-slate-800"
          onClick={() => setSheetOpen(true)}
          data-testid="add-user-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-red-600">{adminCount}</p>
            <p className="text-sm text-slate-500">Admins</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-blue-600">{managerCount}</p>
            <p className="text-sm text-slate-500">Managers</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-slate-600">{workerCount}</p>
            <p className="text-sm text-slate-500">Technicians</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-semibold text-emerald-600">{customerCount}</p>
            <p className="text-sm text-slate-500">Customers</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-users"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full md:w-48 rounded-sm">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roleOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {filteredUsers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id} className={!user.is_active ? "opacity-50" : ""}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-slate-100 text-slate-700">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-sm capitalize ${getRoleColor(user.role)}`}>
                          {user.role === "worker" ? "Technician" : user.role}
                        </Badge>
                        {user.is_primary_admin && (
                          <Badge className="rounded-sm bg-amber-100 text-amber-800 ring-1 ring-amber-600/20">
                            Primary
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.is_active !== false ? (
                        <Badge className="rounded-sm bg-emerald-50 text-emerald-700 ring-emerald-600/20">
                          Active
                        </Badge>
                      ) : (
                        <Badge className="rounded-sm bg-slate-100 text-slate-500 ring-slate-300/20">
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(user)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setUserToReset(user);
                              setResetPasswordDialogOpen(true);
                            }}
                          >
                            <Key className="mr-2 h-4 w-4" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                            {user.is_active !== false ? (
                              <>
                                <UserX className="mr-2 h-4 w-4" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <UserCheck className="mr-2 h-4 w-4" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                            disabled={user.is_primary_admin}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {user.is_primary_admin ? "Cannot Delete Primary Admin" : "Delete"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <UserCog className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No users found
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery || roleFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Start by adding your first user"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit User Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{editingUser ? "Edit User" : "Add User"}</SheetTitle>
            <SheetDescription>
              {editingUser
                ? "Update user information and role"
                : "Create a new user account"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                required
                className="rounded-sm"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                required
                className="rounded-sm"
              />
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter password"
                  required={!editingUser}
                  minLength={6}
                  className="rounded-sm"
                />
                <p className="text-xs text-slate-500">Minimum 6 characters</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value, customer_id: "" })}
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <option.icon className="h-4 w-4" />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {formData.role === "admin" && "Full access to all features and user management"}
                {formData.role === "manager" && "Can manage projects, tasks, and team"}
                {formData.role === "worker" && "Basic access to assigned tasks and projects"}
                {formData.role === "customer" && "Access to customer portal - view profile, installations, and book services"}
              </p>
            </div>
            {formData.role === "customer" && !editingUser && (
              <div className="space-y-2">
                <Label>Link to Customer Profile *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => {
                    const customer = customers.find(c => c.customer_id === value);
                    if (customer) {
                      setFormData({ 
                        ...formData, 
                        customer_id: value,
                        name: customer.name,
                        email: customer.email 
                      });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="customer-select">
                    <SelectValue placeholder="Select customer profile" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No unlinked customers available
                      </SelectItem>
                    ) : (
                      customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.customer_id}>
                          {customer.name} ({customer.email})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Only customers without a user account are shown. Create a customer profile first in the Customers page.
                </p>
              </div>
            )}
            {formData.role === "customer" && editingUser && editingUser.customer_id && (
              <div className="p-3 bg-emerald-50 rounded-sm">
                <p className="text-sm text-emerald-700">
                  Linked to customer profile
                </p>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="save-user-btn"
              >
                {editingUser ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {userToReset?.name}. This will log them out of all
              sessions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={6}
                className="rounded-sm"
              />
              <p className="text-xs text-slate-500">Minimum 6 characters</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setUserToReset(null);
                setNewPassword("");
              }}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{userToDelete?.name}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
